import React, { useState } from "react";
import { firestore, auth } from "../firebase";
import { ToastContainer, toast } from "react-toastify";
import { doc, getDoc, updateDoc, setDoc, arrayUnion } from "firebase/firestore";
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from "react-router-dom";
// Icons for that professional Agri-Tech feel
import { Sprout, Cpu, ShieldCheck, Loader2, ArrowLeft } from "lucide-react"; 

const AddDevice = () => {
  const [deviceId, setDeviceId] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleAddDevice = async () => {
    if (!deviceId || !password) {
      toast.warn("Please enter both Device ID and Password");
      return;
    }

    setIsLoading(true);
    try {
      const deviceRef = doc(firestore, "Devices", deviceId);
      const deviceDoc = await getDoc(deviceRef);

      if (!deviceDoc.exists()) {
        toast.error("Device not found in ATF database");
        setIsLoading(false);
        return;
      }

      const deviceData = deviceDoc.data();
      if (deviceData.password !== password) {
        toast.error("Invalid security credentials");
        setIsLoading(false);
        return;
      }

      if (deviceData.registeredWith === "No") {
        const uid = auth.currentUser.uid;
        
        await updateDoc(deviceRef, { registeredWith: uid });

        const deviceDetails = {
          espId: deviceData.espId,
          name: deviceData.name,
          password: deviceData.password,
          phone: deviceData.phone,
          registeredWith: uid,
          type: deviceData.type,
          addedAt: new Date().toISOString()
        };

        const profileRef = doc(firestore, "Profiles", uid);
        const profileDoc = await getDoc(profileRef);
        
        if (profileDoc.exists()) {
          await updateDoc(profileRef, {
            devices: arrayUnion(deviceDetails)
          });
        } else {
          await setDoc(profileRef, {
            devices: [deviceDetails]
          });
        }

        toast.success("Device linked to ATF account!");
        setTimeout(() => navigate("/"), 1500);
      } else {
        toast.error("This device is already linked to an account.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Connection error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 font-sans">
      <div className="flex flex-col md:flex-row w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100">
        
        {/* Branding Sidebar */}
        <div className="md:w-1/3 bg-gradient-to-br from-emerald-600 to-indigo-700 p-10 text-white flex flex-col justify-between">
          <div>
            {/* <div className="flex items-center space-x-2 mb-8">
              <div className="bg-white p-2 rounded-lg">
                <Sprout className="text-emerald-600" size={24} />
              </div>
              <span className="text-2xl font-black tracking-tighter uppercase italic">ATF</span>
            </div> */}
            <h1 className="text-3xl font-bold leading-tight">
              Any Time <br /> Farming
            </h1>
            <p className="mt-4 text-emerald-100 text-sm leading-relaxed">
              Connect your ATF Smart Nodes to monitor soil health and automate irrigation 24/7.
            </p>
          </div>
          
          <div className="hidden md:block">
            <div className="flex items-center space-x-2 text-xs text-emerald-200 uppercase tracking-widest">
              <div className="h-[1px] w-8 bg-emerald-200"></div>
              <span>Next-Gen Agriculture</span>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="md:w-2/3 p-10 lg:p-16 relative">
          <button 
            onClick={() => navigate("/dashboard")}
            className="absolute top-8 right-8 text-slate-400 hover:text-indigo-600 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>

          <header className="mb-10">
            <h2 className="text-2xl font-bold text-slate-800">Add New Device</h2>
            <p className="text-slate-500 mt-1">Enter your hardware credentials to get started.</p>
          </header>

          <div className="space-y-6">
            <div className="group">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block group-focus-within:text-emerald-600 transition-colors">
                Device Serial ID
              </label>
              <div className="flex items-center border-b-2 border-slate-100 group-focus-within:border-emerald-500 transition-all pb-2">
                <Cpu className="text-slate-300 mr-3" size={20} />
                <input
                  type="text"
                  placeholder="e.g. ATF-NODE-001"
                  className="w-full bg-transparent outline-none text-slate-700 placeholder:text-slate-300 font-medium"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                />
              </div>
            </div>

            <div className="group">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block group-focus-within:text-indigo-600 transition-colors">
                Access Password
              </label>
              <div className="flex items-center border-b-2 border-slate-100 group-focus-within:border-indigo-500 transition-all pb-2">
                <ShieldCheck className="text-slate-300 mr-3" size={20} />
                <input
                  type="password"
                  placeholder="••••••••"
                  className="w-full bg-transparent outline-none text-slate-700 placeholder:text-slate-300 font-medium"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="pt-4">
              <button
                onClick={handleAddDevice}
                disabled={isLoading}
                className="w-full bg-slate-900 hover:bg-black text-white py-4 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform active:scale-95 flex items-center justify-center space-x-3 disabled:bg-slate-300"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  <span>Register Node</span>
                )}
              </button>
            </div>
          </div>

          <footer className="mt-12 text-center">
            <p className="text-slate-400 text-[10px] uppercase tracking-widest font-bold">
              Secure AES-256 Cloud Connection
            </p>
          </footer>
        </div>
      </div>
      
      <ToastContainer 
        theme="colored" 
        position="bottom-right"
        autoClose={3000}
      />
    </div>
  );
};

export default AddDevice;